import { AdvancedMarker, Map as GMap } from "@vis.gl/react-google-maps";
import PlaceIcon from "@mui/icons-material/Place";
import { useMemo, useState } from "react";
import { GetDistinctSurveys, SurveyFullViewModel } from "../../TripRateNew/ViewModels/SurveyFullViewModel";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";
import Popover from '@mui/material/Popover';
import Button from "@mui/material/Button";
import { useLocation, useNavigate } from "react-router-dom";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccordionDetails from "@mui/material/AccordionDetails";
import Divider from "@mui/material/Divider";
import useData from "../../Utilities/Hooks/UseData";
import { RegionAreaViewModel } from "../../TripRate/ViewModels/RegionAreaViewModel";
import TableContainer from "@mui/material/TableContainer";
import Paper from "@mui/material/Paper";
import CompactTable from "../../Utilities/CompactTable";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import { navigateToSite, SiteReturnLabel } from "../SiteNavigation";

type Props = {
    open: boolean;
    onClose: () => void;
    sites: SurveyFullViewModel[];
    returnOrigin?: string;
    navigationType?: string;
    fromTab?: string;
    returnLabel?: SiteReturnLabel;
    returnTo?: string;
};

type SiteMapEntry = ReturnType<typeof GetDistinctSurveys>[number] & {
    regionCode: string | null;
    areaCode: string | null;
    regionDescription: string | null;
    areaDescription: string | null;
};

export default function SiteMapDialog({ open, onClose, sites, returnOrigin, navigationType = "summaryFiltering", fromTab = "surveySummary", returnLabel = "Back to Map", returnTo }: Props) {
    const navigate = useNavigate();
    const location = useLocation();
    const { data: regionAreas } = useData<RegionAreaViewModel[]>("TripRateFilterNew", "GetRegionsAndAreas");
    const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [site, setSite] = useState<SiteMapEntry | null>(null);

    const handlePopoverOpen = (selectedSite: SiteMapEntry) => (event: Event) => {
        const target = event.target;

        setSite(selectedSite);
        setAnchorEl(target instanceof HTMLElement ? target : null);
    };

    const handlePopoverClose = () => {
        setSite(null);
        setAnchorEl(null);
    };

    const openDetail = Boolean(anchorEl);

    const { validSites, invalidSites } = useMemo(() => {
        if (sites && sites.length > 0 && regionAreas && regionAreas.length > 0) {
            const validSites = sites.filter(
                s => s.lat != undefined && s.long != undefined
            );

            const distinctValid = GetDistinctSurveys(validSites);
            const byArea = new Map(regionAreas.map(ra => [ra.areaCode, ra]));

            const distinctWithRegion = distinctValid.map(site => {
                const match = byArea.get(site.area as unknown as string);

                return {
                    ...site,
                    regionCode: match?.regionCode ?? null,
                    areaCode: match?.areaCode ?? null,
                    regionDescription: match?.regionDescription ?? null,
                    areaDescription: match?.areaDescription ?? null,
                };
            });

            const invalidSites = sites
                .filter(s => s.lat == undefined && s.long == undefined)
                .map(s => s.reference);
            const distinctInvalid = [...new Set(invalidSites)];
            return { validSites: distinctWithRegion, invalidSites: distinctInvalid };
        }
        else {
            return { validSites: [], invalidSites: [] };
        }
    }, [sites, regionAreas]);

    const regionTable = useMemo(() => {
        const regionCounts = new Map<string, { regionCode: string; regionDescription: string; count: number }>();
        validSites.forEach(site => {
            if (site.regionCode) {
                if (!regionCounts.has(site.regionCode)) {
                    regionCounts.set(site.regionCode, {
                        regionCode: site.regionCode,
                        regionDescription: site.regionDescription || "",
                        count: 0
                    });
                }
                regionCounts.get(site.regionCode)!.count += 1;
            }
        });
        return Array.from(regionCounts.values()).sort((a, b) => a.regionCode.localeCompare(b.regionCode));
    }, [validSites]);

    const areaTable = useMemo(() => {
        let filteredSites = validSites;
        if (selectedRegion) {
            filteredSites = validSites.filter(site => site.regionCode === selectedRegion);
        }
        return filteredSites;
    }, [validSites, selectedRegion]);

    const selectRegion = (code) => () => {
        setSelectedRegion(code);
    }

    const setSiteId = (reference) => () => {
        navigateToSite(navigate, location, {
            siteRef: reference,
            type: navigationType,
            fromTab,
            returnLabel,
            returnTo,
            extraParams: { origin: returnOrigin }
        });
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl" PaperProps={{ sx: { backgroundColor: '#f2f4f8' } }}>
            <DialogContent sx={{ height: 700, p: 0 }}>
                <Grid container height="100%">
                    <Grid size={6} height="100%">
                        <GMap
                            defaultZoom={6}
                            defaultCenter={{ lat: 54.534, lng: -3.717 }}
                            mapId="DEMO_MAP_ID"
                            style={{ width: "100%", height: "100%" }}
                        >
                            {areaTable.map(site => (
                                <AdvancedMarker
                                    key={site.reference}
                                    position={{
                                        lat: site.lat!,
                                        lng: site.long!
                                    }}
                                    onClick={handlePopoverOpen(site)}
                                >
                                    <PlaceIcon color="primary" />
                                </AdvancedMarker>
                            ))}
                        </GMap>
                    </Grid>
                    <Grid size={6} container height="100%" overflow="auto">
                        <Grid size={12}>
                            {invalidSites.length > 0 && (
                                <Accordion defaultExpanded>
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}>
                                        Sites without valid location data:
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {invalidSites.map(ref => (
                                            <Typography
                                                variant="body2"
                                                sx={{ mb: 0.25, fontSize: "0.8rem" }}
                                            >
                                                {ref}
                                            </Typography>
                                        ))}
                                    </AccordionDetails>
                                </Accordion>
                            )}
                        </Grid>
                        <Grid size={12}>
                            <TableContainer component={Paper} sx={{ mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ p: 2, pb: 0 }}>
                                    Regions
                                </Typography>
                                <Divider sx={{ mb: 1 }} />
                                <CompactTable>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: "bold" }}>Region</TableCell>
                                            <TableCell sx={{ fontWeight: "bold" }}>
                                                Survey Count
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {regionTable.map((reg) => (
                                            <TableRow
                                                key={reg.regionCode}
                                                hover
                                                sx={{
                                                    cursor: "pointer",
                                                    "&:hover": { backgroundColor: "action.hover" },
                                                }}
                                                onClick={selectRegion(reg.regionCode)}
                                            >
                                                <TableCell>{reg.regionDescription}</TableCell>
                                                <TableCell>{reg.count}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </CompactTable>
                            </TableContainer>
                        </Grid>
                        <Grid size={12}>
                            <TableContainer component={Paper} sx={{ mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ p: 2, pb: 0 }}>
                                    Surveys for {selectedRegion !== null ? `${regionAreas?.find(f => f.regionCode == selectedRegion)?.regionDescription} Region` : "All Regions"} 
                                </Typography>
                                <Divider sx={{ mb: 1 }} />

                                <CompactTable>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: "bold" }}>Area</TableCell>
                                            <TableCell sx={{ fontWeight: "bold" }}>Survey Reference</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {areaTable.map((surv) => (
                                            <TableRow
                                                key={surv.reference}
                                                hover
                                                sx={{
                                                    cursor: "pointer",
                                                    "&:hover": { backgroundColor: "action.hover" },
                                                }}
                                               
                                            >
                                                <TableCell>{surv.areaDescription}</TableCell>
                                                <TableCell>{surv.reference}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </CompactTable>
                            </TableContainer>
                        </Grid>
                    </Grid>
                </Grid>
            </DialogContent>
            <Popover
                open={openDetail}
                anchorEl={anchorEl}
                onClose={handlePopoverClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
            >
                <Grid container width="340px">
                    <Grid size={6}>
                        <Typography>Site: {site?.reference}</Typography>
                    </Grid>
                    <Grid size={3}>
                        <Button onClick={setSiteId(site?.reference)} >View</Button>
                    </Grid>
                    <Grid size={3}>
                        <Button onClick={handlePopoverClose}>Close</Button>
                    </Grid>

                    <Grid size={6}>
                        <Typography textAlign="right">Description</Typography>
                    </Grid>
                    <Grid size={6}>
                        <Typography>{site?.description}</Typography>
                    </Grid>
                    <Grid size={6}>
                        <Typography textAlign="right">Town</Typography>
                    </Grid>
                    <Grid size={6}>
                        <Typography>{site?.town}</Typography>
                    </Grid>
                    <Grid size={6}>
                        <Typography textAlign="right">Area</Typography>
                    </Grid>
                    <Grid size={6}>
                        <Typography>{site?.areaDescription}</Typography>
                    </Grid>
                    <Grid size={6}>
                        <Typography textAlign="right">Location</Typography>
                    </Grid>
                    <Grid size={6}>
                        <Typography>{site?.location}</Typography>
                    </Grid>
                    <Grid size={6}>
                        <Typography textAlign="right">{site?.paramLabels[0]}</Typography>
                    </Grid>
                    <Grid size={6}>
                        <Typography>{site?.param1}</Typography>
                    </Grid>
                    <Grid size={6}>
                        <Typography textAlign="right">{site?.paramLabels[1]}</Typography>
                    </Grid>
                    <Grid size={6}>
                        <Typography>{site?.param2}</Typography>
                    </Grid>
                    <Grid size={6}>
                        <Typography textAlign="right">{site?.paramLabels[2]}</Typography>
                    </Grid>
                    <Grid size={6}>
                        <Typography>{site?.param3}</Typography>
                    </Grid>
                    <Grid size={6}>
                        <Typography textAlign="right">{site?.paramLabels[3]}</Typography>
                    </Grid>
                    <Grid size={6}>
                        <Typography>{site?.param4}</Typography>
                    </Grid>
                </Grid>
            </Popover>
        </Dialog>
    );

}
